/**
 * Auto-scheduling around labor availability (docs/features/auto-scheduling.md).
 *
 * Pure scoring for "which cleaner, which slot" when a sold quote is booked.
 * The Worker gathers the facts (open slots from the ordinary slot search,
 * the cleaner's jobs that day, where the previous one was) and this file turns
 * them into a ranked list. Nothing here assigns anybody: the dispatcher is
 * shown the top proposals and picks one.
 *
 * Days and hours are NOT stored here: they are the person's existing weekly
 * availability (availability_patterns, 0067). What this adds is the rest of
 * the profile: how many jobs a day, how far they travel, and what kind of work
 * they are cleared for.
 */

/**
 * The kinds of work a cleaner can be cleared for. The first two match
 * QUOTE_SEGMENTS; the other two are specialist jobs a quote can call for.
 */
export const CLEANER_SKILLS = ['residential', 'commercial', 'deep_clean', 'move_out'] as const;
export type CleanerSkill = (typeof CLEANER_SKILLS)[number];
/** Skills that follow the quote's segment. A mismatch can be a hard rule. */
export const SEGMENT_SKILLS = ['residential', 'commercial'] as const satisfies readonly CleanerSkill[];
/** Specialist skills. A cleaner without one is ranked lower, never left out. */
export const JOB_SKILLS = ['deep_clean', 'move_out'] as const satisfies readonly CleanerSkill[];
export type JobSkill = (typeof JOB_SKILLS)[number];

/** A cleaner's optional home base. Only owners and managers see or change it. */
export interface CleanerHomeAddress {
  addressLine1: string;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  countryCode: string | null;
  /** True once the address lookup has placed it on the map. */
  located: boolean;
}

/** How many proposals the dispatcher sees. */
export const CLEANER_PROPOSAL_LIMIT = 3;
/** Weekly bookings are checked, and reserved, this many weeks ahead. */
export const RECURRING_RESERVATION_WEEKS = 12;

export interface CleanerSchedulingProfile {
  userId: string;
  /** Null = no daily cap. */
  maxJobsPerDay: number | null;
  /** Null = no travel limit. Kilometres, the unit the distance math uses. */
  travelRadiusKm: number | null;
  skills: CleanerSkill[];
  /**
   * Home base. Undefined when the reader may not see it (not an owner or
   * manager); null when none is on file.
   */
  homeAddress?: CleanerHomeAddress | null;
  /** False when nothing was saved and these are the defaults. */
  saved: boolean;
  revision: number;
}

/** A person with no saved profile can do any kind of work, any amount, anywhere. */
export function defaultSchedulingProfile(userId: string): CleanerSchedulingProfile {
  return { userId, maxJobsPerDay: null, travelRadiusKm: null, skills: [...CLEANER_SKILLS], saved: false, revision: 0 };
}

/**
 * Scoring weights. Each feature is normalised to 0..1 (1 = best), multiplied by
 * its weight, and summed. Penalties are subtracted per unit.
 *
 * Tuned without a code change through the `SCHEDULING_SCORE_WEIGHTS` Worker
 * variable: a JSON object with any subset of these keys.
 */
export interface SchedulingWeights {
  /** Short drive from the previous job that day, or from home for the first job. */
  distance: number;
  /** Cleared for the kind of work the quote is (residential/commercial). */
  skill: number;
  /** Holds the specialist skills (deep clean, move-out) the quote calls for. Never a hard rule. */
  jobSkill: number;
  /** Room left under the daily cap after adding this job. */
  capacity: number;
  /** Earlier date, so the customer is served sooner. */
  earliness: number;
  /** Subtracted for every future weekly occurrence that would clash. */
  recurringClashPenalty: number;
  /** Distance at which the distance score reaches zero when a cleaner has no radius. */
  distanceFalloffKm: number;
  /** Leave out cleaners not cleared for the work (true), or only score them down (false). */
  excludeSkillMismatch: boolean;
  /** Leave out slots that push a cleaner past their cap (true), or only score them down. */
  excludeOverCap: boolean;
  /** Leave out slots beyond the cleaner's travel radius (true), or only score them down. */
  excludeOutsideRadius: boolean;
}

export const DEFAULT_SCHEDULING_WEIGHTS: Readonly<SchedulingWeights> = Object.freeze({
  distance: 3,
  skill: 4,
  jobSkill: 2,
  capacity: 2,
  earliness: 1,
  recurringClashPenalty: 0.5,
  distanceFalloffKm: 40,
  excludeSkillMismatch: true,
  excludeOverCap: true,
  excludeOutsideRadius: true,
});

/**
 * Reads `SCHEDULING_SCORE_WEIGHTS`. A malformed value, or a bad key inside it,
 * falls back to the default for that key: a typo in a variable must never take
 * the scheduler down.
 */
export function parseSchedulingWeights(raw: string | null | undefined): SchedulingWeights {
  const weights: SchedulingWeights = { ...DEFAULT_SCHEDULING_WEIGHTS };
  if (!raw || !raw.trim()) return weights;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return weights;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return weights;
  const input = parsed as Record<string, unknown>;
  for (const key of Object.keys(DEFAULT_SCHEDULING_WEIGHTS) as Array<keyof SchedulingWeights>) {
    const value = input[key];
    const fallback = DEFAULT_SCHEDULING_WEIGHTS[key];
    if (typeof fallback === 'boolean') {
      if (typeof value === 'boolean') (weights as unknown as Record<string, unknown>)[key] = value;
    } else if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
      if (key === 'distanceFalloffKm' && value === 0) continue;
      (weights as unknown as Record<string, unknown>)[key] = value;
    }
  }
  return weights;
}

export interface GeoPoint {
  lat: number;
  lng: number;
}

/** Great-circle distance in kilometres. Straight line, not road distance. */
export function distanceKm(a: GeoPoint, b: GeoPoint): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)));
}

export const KM_PER_MILE = 1.609344;

/** One open slot for one cleaner, with the facts the score needs. */
export interface CleanerCandidate {
  userId: string;
  name: string;
  startsOn: string;
  startTimeLocal: string;
  /** Open jobs the cleaner already has on that local date. */
  jobsThatDay: number;
  /**
   * Km from where the cleaner sets off: the previous job that day, or home
   * for the first job. Null when unknown. Straight line today; the travel
   * estimator (worker/services/travel-distance.ts) can swap in drive distance.
   */
  kmFromPreviousJob: number | null;
  /** Where the trip starts: previous job, home, or nowhere known (first job, no home on file). */
  travelFrom: 'previous_job' | 'home' | 'none';
  /** Days between the search start and this slot. */
  daysOut: number;
  /** Future occurrences checked (1 for a one-time booking). */
  occurrencesChecked: number;
  occurrencesClashing: number;
}

export type ProposalFlag =
  'skill_mismatch' | 'over_daily_cap' | 'outside_radius' | 'distance_unknown' | 'recurring_clash' | 'missing_job_skill';

/** What a quote asks of the cleaner. */
export interface SkillRequirement {
  /** Residential or commercial; null when the quote does not say. */
  segment: CleanerSkill | null;
  /** Deep clean and/or move-out, inferred from the quote. */
  jobSkills: JobSkill[];
}

export interface ScoredProposal extends CleanerCandidate {
  score: number;
  breakdown: {
    distance: number;
    skill: number;
    jobSkill: number;
    capacity: number;
    earliness: number;
    recurringPenalty: number;
  };
  flags: ProposalFlag[];
}

/**
 * Scores one candidate, or returns null when a hard rule (from the weights)
 * leaves it out. A null segment means the quote does not say. Missing a
 * specialist job skill only lowers the score.
 */
export function scoreCandidate<T extends CleanerCandidate>(
  candidate: T,
  profile: CleanerSchedulingProfile,
  requirement: SkillRequirement,
  weights: SchedulingWeights,
  searchDays: number,
): (T & ScoredProposal) | null {
  const flags: ProposalFlag[] = [];
  const requiredSkill = requirement.segment;

  const skillOk = requiredSkill === null || profile.skills.includes(requiredSkill);
  if (!skillOk) {
    if (weights.excludeSkillMismatch) return null;
    flags.push('skill_mismatch');
  }

  const after = candidate.jobsThatDay + 1;
  const cap = profile.maxJobsPerDay;
  const overCap = cap !== null && after > cap;
  if (overCap) {
    if (weights.excludeOverCap) return null;
    flags.push('over_daily_cap');
  }

  const km = candidate.kmFromPreviousJob;
  const radius = profile.travelRadiusKm;
  if (km !== null && radius !== null && km > radius) {
    if (weights.excludeOutsideRadius) return null;
    flags.push('outside_radius');
  }
  if (km === null && candidate.travelFrom !== 'none') flags.push('distance_unknown');

  // First job of the day with no home on file: nothing to weigh, so no penalty.
  // A starting point without coordinates: neutral, so it neither wins nor loses on distance.
  const falloff = radius ?? weights.distanceFalloffKm;
  const distanceScore =
    km === null ? (candidate.travelFrom === 'none' ? 1 : 0.5) : Math.max(0, 1 - km / Math.max(falloff, 0.001));
  const skillScore = skillOk ? 1 : 0;
  const missingJobSkills = requirement.jobSkills.filter((s) => !profile.skills.includes(s));
  if (missingJobSkills.length > 0) flags.push('missing_job_skill');
  const jobSkillScore =
    requirement.jobSkills.length === 0 ? 1 : 1 - missingJobSkills.length / requirement.jobSkills.length;
  // Share of the day's cap still free before this job: a cleaner with room is preferred to one about to fill up.
  const capacityScore = cap === null ? 1 : overCap || cap <= 0 ? 0 : (cap - candidate.jobsThatDay) / cap;
  const earlinessScore = searchDays <= 0 ? 1 : Math.max(0, 1 - candidate.daysOut / searchDays);
  if (candidate.occurrencesClashing > 0) flags.push('recurring_clash');

  const breakdown = {
    distance: round(weights.distance * distanceScore),
    skill: round(weights.skill * skillScore),
    jobSkill: round(weights.jobSkill * jobSkillScore),
    capacity: round(weights.capacity * capacityScore),
    earliness: round(weights.earliness * earlinessScore),
    recurringPenalty: round(weights.recurringClashPenalty * candidate.occurrencesClashing),
  };
  const score = round(
    breakdown.distance +
      breakdown.skill +
      breakdown.jobSkill +
      breakdown.capacity +
      breakdown.earliness -
      breakdown.recurringPenalty,
  );
  return { ...candidate, score, breakdown, flags };
}

const round = (value: number) => Math.round(value * 1000) / 1000;

/**
 * The top proposals, at most one per cleaner-and-day so three picks are three
 * real choices rather than the same person at 9:00, 9:30 and 10:00.
 * Ties break on date, then time, then name, so the order is stable.
 */
export function rankProposals<T extends CleanerCandidate>(
  candidates: readonly T[],
  profiles: ReadonlyMap<string, CleanerSchedulingProfile>,
  requirement: SkillRequirement,
  weights: SchedulingWeights,
  searchDays: number,
  limit = CLEANER_PROPOSAL_LIMIT,
): Array<T & ScoredProposal> {
  const scored = candidates
    .map((c) =>
      scoreCandidate(c, profiles.get(c.userId) ?? defaultSchedulingProfile(c.userId), requirement, weights, searchDays),
    )
    .filter((p): p is T & ScoredProposal => p !== null)
    .sort(
      (a, b) =>
        b.score - a.score ||
        `${a.startsOn}/${a.startTimeLocal}`.localeCompare(`${b.startsOn}/${b.startTimeLocal}`) ||
        a.name.localeCompare(b.name) ||
        a.userId.localeCompare(b.userId),
    );
  const seen = new Set<string>();
  const out: Array<T & ScoredProposal> = [];
  for (const proposal of scored) {
    const key = `${proposal.userId}/${proposal.startsOn}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(proposal);
    if (out.length >= limit) break;
  }
  return out;
}

/** The skill a quote needs, from its segment. Unknown segments require nothing. */
export function requiredSkillForSegment(segment: string | null | undefined): CleanerSkill | null {
  return segment === 'residential' || segment === 'commercial' ? segment : null;
}

/** Countries that give road distances in miles. Everyone else reads km. */
export function usesMiles(countryCode: string | null | undefined): boolean {
  return ['US', 'GB', 'LR', 'MM'].includes((countryCode ?? '').toUpperCase());
}

/**
 * The specialist skills a quote calls for.
 *
 * Quotes carry no "service type" column. What marks a deep clean or a
 * move-out is (a) the job-type tags on the room and task templates the quote
 * was built from (template-taxonomy.ts: 'standard' | 'deep' | 'move_out' |
 * 'post_construction') and (b) the property's occupancy status.
 *
 * - move_out: the property is marked move-out, or a template is tagged
 *   move-out only (not also standard or deep — a task shared by deep and
 *   move-out jobs proves neither on its own).
 * - deep_clean: a template tagged deep and not standard, so an everyday task
 *   that also appears in deep cleans does not count.
 */
export function jobSkillsForQuote(input: {
  templateJobTypes: ReadonlyArray<readonly string[]>;
  occupancyStatus: string | null | undefined;
}): JobSkill[] {
  const out = new Set<JobSkill>();
  if (input.occupancyStatus === 'move_out') out.add('move_out');
  for (const types of input.templateJobTypes) {
    if (types.includes('standard')) continue;
    if (types.includes('deep')) out.add('deep_clean');
    else if (types.includes('move_out')) out.add('move_out');
  }
  return JOB_SKILLS.filter((s) => out.has(s));
}
